import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@components/common';
import './DonationForm.css';

interface DonationFormProps {
  amount: number;
  onSubmit: (email: string, discord: string | undefined, amount: number) => Promise<void>;
  onBack: () => void;
  loading?: boolean;
}

export const DonationForm: React.FC<DonationFormProps> = ({
  amount: initialAmount,
  onSubmit,
  onBack,
  loading = false,
}) => {
  const [email, setEmail] = useState('');
  const [discord, setDiscord] = useState('');
  const [amount, setAmount] = useState(initialAmount);
  const [errors, setErrors] = useState<{ email?: string }>({});

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speedRef = useRef(150);

  const stopHold = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    speedRef.current = 150;
  }, []);

  const startHold = useCallback((direction: 'increase' | 'decrease') => {
    const updateAmount = () => {
      setAmount(prev => {
        if (direction === 'increase') {
          return prev + 1;
        } else {
          return Math.max(1, prev - 1);
        }
      });
    };

    updateAmount();

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        updateAmount();
        if (speedRef.current > 30) {
          speedRef.current -= 10;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(updateAmount, speedRef.current);
          }
        }
      }, speedRef.current);
    }, 250);
  }, []);

  useEffect(() => {
    return () => stopHold();
  }, [stopHold]);

  const validate = (): boolean => {
    const newErrors: { email?: string } = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    await onSubmit(email, discord || undefined, amount);
  };

  return (
    <form onSubmit={handleSubmit} className="donation-form">
      <div className="amount-adjuster-wrapper">
        {amount < 10 && (
          <div className="amount-adjuster__warning">
            Min 10€ required to receive archive password
          </div>
        )}
        <div className="amount-adjuster">
          <button
            type="button"
            className="amount-adjuster__btn amount-adjuster__btn--decrease"
            onMouseDown={() => startHold('decrease')}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={() => startHold('decrease')}
            onTouchEnd={stopHold}
            disabled={amount <= 1 || loading}
            aria-label="Decrease amount"
          >
            −
          </button>

          <div className="amount-adjuster__display">
            <span className="amount-adjuster__value">{amount}</span>
            <span className="amount-adjuster__currency">€</span>
          </div>

          <button
            type="button"
            className="amount-adjuster__btn amount-adjuster__btn--increase"
            onMouseDown={() => startHold('increase')}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={() => startHold('increase')}
            onTouchEnd={stopHold}
            disabled={loading}
            aria-label="Increase amount"
          >
            +
          </button>
        </div>
      </div>

      <div className="donation-form__group">
        <label htmlFor="email" className="donation-form__label">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className={`form-input ${errors.email ? 'form-input--error' : ''}`}
          disabled={loading}
        />
        {errors.email ? (
          <span className="donation-form__error">{errors.email}</span>
        ) : (
          <span className="donation-form__helper">
            Email required for assuring delivery of donation benefits
          </span>
        )}
      </div>

      <div className="donation-form__group">
        <label htmlFor="discord" className="donation-form__label">
          Discord Username <span className="donation-form__optional">(optional)</span>
        </label>
        <input
          type="text"
          id="discord"
          value={discord}
          onChange={(e) => setDiscord(e.target.value)}
          placeholder="username"
          className="form-input"
          disabled={loading}
        />
        <span className="donation-form__helper">
          For Discord benefits like roles or support
        </span>
      </div>

      <div className="donation-form__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onBack}
          disabled={loading}
        >
          ← Back
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          fullWidth
        >
          Continue to Payment
        </Button>
      </div>
    </form>
  );
};

export default DonationForm;
